import React, { useState, useEffect } from 'react';
import MonitoringDashboard from './MonitoringDashboard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function CapacitySplitModal({ constellation, token, onClose, onSaved }) {
  const total = {
    vcpus: constellation.total_vcpus || 0,
    ram: constellation.total_ram_gb || 0,
    storage: constellation.total_storage_gb || 0,
  };
  const [privateVCPUs, setPrivateVCPUs] = useState(constellation.private_vcpus || 0);
  const [privateRAM, setPrivateRAM] = useState(constellation.private_ram_gb || 0);
  const [privateStorage, setPrivateStorage] = useState(constellation.private_storage_gb || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/v1/operator/constellations/${constellation.id}/capacity`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          private_vcpus: parseInt(privateVCPUs),
          private_ram_gb: parseInt(privateRAM),
          private_storage_gb: parseInt(privateStorage),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const SliderRow = ({ label, value, setValue, max, unit }) => (
    <div className="mb-6">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-300">{label}</span>
        <span className="text-sm text-gray-400">
          Private: <span className="text-white font-bold">{value}{unit}</span>
          &nbsp;/ Public: <span className="text-green-400 font-bold">{max - value}{unit}</span>
          &nbsp;/ Total: {max}{unit}
        </span>
      </div>
      <input
        type="range" min={0} max={max} value={value}
        onChange={e => setValue(Number(e.target.value))}
        className="w-full accent-blue-500"
      />
      <div className="flex mt-1 rounded overflow-hidden h-2">
        <div className="bg-blue-600 transition-all" style={{ width: `${max > 0 ? (value/max*100) : 0}%` }} />
        <div className="bg-green-600 transition-all flex-1" />
      </div>
      <div className="flex text-xs text-gray-500 mt-1 justify-between">
        <span>🔒 Private (your customers)</span>
        <span>🌐 Public marketplace</span>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-600 shadow-2xl">
        <h2 className="text-xl font-bold text-white mb-1">Capacity Split</h2>
        <p className="text-gray-400 text-sm mb-6">{constellation.name} — drag to adjust public/private allocation</p>

        <SliderRow label="vCPUs" value={privateVCPUs} setValue={setPrivateVCPUs} max={total.vcpus} unit="" />
        <SliderRow label="RAM" value={privateRAM} setValue={setPrivateRAM} max={total.ram} unit=" GB" />
        <SliderRow label="Storage" value={privateStorage} setValue={setPrivateStorage} max={total.storage} unit=" GB" />

        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        <div className="flex space-x-3">
          <button onClick={save} disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg font-medium">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg font-medium">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function OperatorDashboard() {
  const [constellations, setConstellations] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [refCode, setRefCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [capacityModal, setCapacityModal] = useState(null);

  const token = localStorage.getItem('operator_token');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [constRes, revenueRes, customersRes, refRes] = await Promise.all([
        fetch(`${API_URL}/v1/operator/constellations`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/v1/operator/revenue`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/v1/operator/customers`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/v1/operator/ref-code`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);

      const constData = await constRes.json();
      const revenueData = await revenueRes.json();
      const customersData = await customersRes.json();
      const refData = await refRes.json();

      setConstellations(constData.constellations || []);
      setRevenue(revenueData);
      setCustomers(customersData.customers || []);
      setRefCode(refData.ref_code || '');
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setLoading(false);
    }
  };

  const generateActivationCode = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/operator/activation-codes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      alert(`Activation Code: ${data.activation_code}\n\nUse this to register a new server node.`);
    } catch (err) {
      alert('Failed to generate activation code: ' + err.message);
    }
  };

  const copySignupLink = () => {
    const url = `${window.location.origin}/register?ref=${refCode}`;
    navigator.clipboard.writeText(url);
    alert(`Copied!\n\n${url}\n\nShare this link with your customers to link them to your capacity.`);
  };

  const totalCapacity = constellations.reduce((acc, c) => ({
    cpu: acc.cpu + (c.capacity?.vcpus?.public || 0),
    ram: acc.ram + (c.capacity?.ram_gb?.public || 0),
    storage: acc.storage + (c.capacity?.storage_gb?.public || 0),
    privateCpu: acc.privateCpu + (c.private_vcpus || 0),
    privateRam: acc.privateRam + (c.private_ram_gb || 0),
    privateStorage: acc.privateStorage + (c.private_storage_gb || 0),
  }), { cpu: 0, ram: 0, storage: 0, privateCpu: 0, privateRam: 0, privateStorage: 0 });

  const allocatedCapacity = constellations.reduce((acc, c) => ({
    cpu: acc.cpu + (c.capacity?.vcpus?.allocated || 0),
    ram: acc.ram + (c.capacity?.ram_gb?.allocated || 0),
    storage: acc.storage + (c.capacity?.storage_gb?.allocated || 0),
  }), { cpu: 0, ram: 0, storage: 0 });

  const tabs = ['overview', 'customers', 'monitoring'];

  if (loading) return <div className="flex items-center justify-center min-h-screen text-white">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-900">
      <nav className="bg-gray-800 shadow-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-6">
              <h1 className="text-xl font-bold text-white">Operator Dashboard</h1>
              <div className="flex space-x-1">
                {tabs.map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`px-3 py-2 rounded-md text-sm font-medium capitalize transition ${
                      activeTab === tab ? 'bg-blue-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}>
                    {tab === 'monitoring' ? '📊 Monitoring' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={generateActivationCode}
                className="bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700">
                + Activation Code
              </button>
              {refCode && (
                <button onClick={copySignupLink}
                  className="bg-green-700 text-white px-3 py-2 rounded-md text-sm hover:bg-green-600">
                  📋 Copy Signup Link
                </button>
              )}
              <a href="/admin/dashboard" className="text-gray-400 hover:text-white text-sm">⚙️ Admin</a>
              <button onClick={() => { localStorage.removeItem('operator_token'); window.location.href = '/operator/login'; }}
                className="text-gray-400 hover:text-white text-sm">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

        {activeTab === 'monitoring' && <MonitoringDashboard />}

        {activeTab === 'customers' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white">Your Customers</h2>
              {refCode && (
                <div className="flex items-center space-x-3">
                  <span className="text-gray-400 text-sm">Signup link:</span>
                  <code className="bg-gray-800 text-green-400 px-3 py-1 rounded text-sm border border-gray-600">
                    {window.location.origin}/register?ref={refCode}
                  </code>
                  <button onClick={copySignupLink}
                    className="bg-green-700 text-white px-3 py-1 rounded text-sm hover:bg-green-600">
                    Copy
                  </button>
                </div>
              )}
            </div>
            {customers.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-12 text-center border border-gray-700">
                <p className="text-gray-400 mb-2">No customers yet.</p>
                <p className="text-gray-500 text-sm">Share your signup link to link customers to your capacity.</p>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-700">
                    <tr>
                      {['Name', 'Email', 'Status', 'Joined'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {customers.map(cu => (
                      <tr key={cu.id}>
                        <td className="px-6 py-4 text-sm text-white">{cu.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-300">{cu.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${cu.status === 'active' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'}`}>
                            {cu.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">
                          {new Date(cu.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'overview' && (
          <>
            {/* Revenue */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {[
                { label: 'This Month', value: revenue?.this_month, note: 'Estimated earnings (70% share)' },
                { label: 'Last Month', value: revenue?.last_month, note: 'Total payout' },
                { label: 'All Time', value: revenue?.total, note: 'Total earned' },
              ].map(({ label, value, note }) => (
                <div key={label} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">{label}</h3>
                  <p className="text-3xl font-bold text-white">${(value || 0).toFixed(2)}</p>
                  <p className="text-sm text-gray-500 mt-1">{note}</p>
                </div>
              ))}
            </div>

            {/* Capacity Overview */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Capacity Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'CPU', alloc: allocatedCapacity.cpu, pub: totalCapacity.cpu, priv: totalCapacity.privateCpu, unit: 'vCPUs', color: 'blue' },
                  { label: 'RAM', alloc: allocatedCapacity.ram, pub: totalCapacity.ram, priv: totalCapacity.privateRam, unit: 'GB', color: 'green' },
                  { label: 'Storage', alloc: allocatedCapacity.storage, pub: totalCapacity.storage, priv: totalCapacity.privateStorage, unit: 'GB', color: 'purple' },
                ].map(({ label, alloc, pub, priv, unit, color }) => {
                  const pct = pub > 0 ? (alloc / pub * 100).toFixed(1) : 0;
                  return (
                    <div key={label}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-300">{label}</span>
                        <span className="text-sm text-gray-400">{pct}% used</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                        <div className={`bg-${color}-600 h-2 rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-xs text-gray-500">
                        {alloc}/{pub} {unit} public · {priv} {unit} private
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Constellations */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">Your Constellations</h3>
              </div>
              {constellations.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400 mb-4">No constellations yet.</p>
                  <button onClick={generateActivationCode}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
                    Generate Activation Code
                  </button>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-700">
                    <tr>
                      {['Name', 'Region', 'Status', 'Public Capacity', 'Private Capacity', 'Instances', 'Last Seen', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {constellations.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-750">
                        <td className="px-4 py-4 text-sm font-medium text-white">{c.name}</td>
                        <td className="px-4 py-4 text-sm text-gray-400">{c.region}</td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            c.status === 'active' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'
                          }`}>{c.status}</span>
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-400">
                          CPU: {c.capacity?.vcpus?.allocated || 0}/{c.capacity?.vcpus?.public || 0}<br/>
                          RAM: {c.capacity?.ram_gb?.allocated || 0}/{c.capacity?.ram_gb?.public || 0}GB
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-400">
                          CPU: {c.private_vcpus || 0} vCPUs<br/>
                          RAM: {c.private_ram_gb || 0} GB
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-400">{c.instance_count || 0}</td>
                        <td className="px-4 py-4 text-sm text-gray-400">
                          {c.last_heartbeat ? new Date(c.last_heartbeat).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={() => setCapacityModal(c)}
                            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded border border-gray-600">
                            Split
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>

      {capacityModal && (
        <CapacitySplitModal
          constellation={capacityModal}
          token={token}
          onClose={() => setCapacityModal(null)}
          onSaved={fetchData}
        />
      )}
    </div>
  );
}

export default OperatorDashboard;
